import type { Color } from "../../common/color.js";
import type { Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { drawCheckmark, drawCross, drawHamburger, drawTriangle } from "./glyphs.js";

/** Everything a glyph needs to draw itself. */
export interface GlyphContext {
  rc: RenderContext;
  /** Square area to draw inside, in local coordinates. */
  box: Rect;
  ink: Color;
  /** Nominal stroke weight for the box, already rounded to whole pixels. */
  weight: number;
}

export type GlyphPainter = (g: GlyphContext) => void;

const registry = new Map<string, GlyphPainter>();

/**
 * Add a glyph, or replace one.
 *
 * Named rather than passed as a function so a glyph can be chosen from data —
 * a toolbar spec, a config file — the same way `registerFlare` works. Register
 * before the control that uses it is painted; nothing caches the lookup.
 */
export function registerGlyph(name: string, paint: GlyphPainter): void {
  registry.set(name, paint);
}

/** The painter for `name`, or null when nothing is registered under it. */
export function createGlyph(name: string): GlyphPainter | null {
  return registry.get(name) ?? null;
}

/** Every registered name, for a picker or a test. */
export function glyphNames(): string[] {
  return [...registry.keys()].sort();
}

/** Draw `name` if it exists; returns false when it does not. */
export function paintGlyph(name: string, g: GlyphContext): boolean {
  const paint = registry.get(name);
  if (!paint) return false;
  paint(g);
  return true;
}

// --- the built-in set -----------------------------------------------------
//
// Everything here is drawn from the three primitives the renderer has: rects,
// strokes and filled triangles. Nothing needs a font, so a glyph button looks
// the same whatever is installed, and scales without going blurry.

const side = (box: Rect): number => Math.min(box.width, box.height);
const center = (box: Rect): { x: number; y: number } => ({
  x: box.x + box.width / 2,
  y: box.y + box.height / 2,
});

/** A bar of `weight` thickness, centered, `length` long, along one axis. */
function bar(g: GlyphContext, length: number, vertical: boolean): void {
  const c = center(g.box);
  const w = vertical ? g.weight : length;
  const h = vertical ? length : g.weight;
  g.rc.fillRect(
    { x: Math.round(c.x - w / 2), y: Math.round(c.y - h / 2), width: w, height: h },
    g.ink,
  );
}

/** Two strokes meeting at a point — the chevron shape, not a filled arrow. */
function chevron(g: GlyphContext, direction: "up" | "down" | "left" | "right"): void {
  const c = center(g.box);
  const s = side(g.box) * 0.3;
  const horizontal = direction === "left" || direction === "right";
  const sign = direction === "up" || direction === "left" ? -1 : 1;
  const tip = horizontal ? { x: c.x + s * sign, y: c.y } : { x: c.x, y: c.y + s * sign };
  const a = horizontal ? { x: c.x - s * sign, y: c.y - s } : { x: c.x - s, y: c.y - s * sign };
  const b = horizontal ? { x: c.x - s * sign, y: c.y + s } : { x: c.x + s, y: c.y - s * sign };
  g.rc.drawPolyline([a, tip, b], g.weight, g.ink);
}

/** `count` dots along one axis, as a menu or overflow affordance. */
function dots(g: GlyphContext, vertical: boolean, count = 3): void {
  const c = center(g.box);
  const d = Math.max(2, Math.round(side(g.box) * 0.14));
  const gap = Math.max(d + 1, Math.round(side(g.box) * 0.22));
  const span = count * d + (count - 1) * gap;
  for (let i = 0; i < count; i++) {
    const offset = -span / 2 + i * (d + gap) + d / 2;
    g.rc.fillRect(
      {
        x: Math.round((vertical ? c.x : c.x + offset) - d / 2),
        y: Math.round((vertical ? c.y + offset : c.y) - d / 2),
        width: d,
        height: d,
      },
      g.ink,
      d / 2,
    );
  }
}

registerGlyph("check", (g) => drawCheckmark(g.rc, g.box, g.ink));
registerGlyph("close", (g) => drawCross(g.rc, g.box, g.ink, Math.round(side(g.box) * 0.62)));
registerGlyph("hamburger", (g) => drawHamburger(g.rc, g.box, g.ink));
registerGlyph("ellipsis", (g) => dots(g, false));
registerGlyph("kebab", (g) => dots(g, true));

registerGlyph("plus", (g) => {
  const length = Math.round(side(g.box) * 0.62);
  bar(g, length, false);
  bar(g, length, true);
});
registerGlyph("minus", (g) => bar(g, Math.round(side(g.box) * 0.62), false));

for (const direction of ["up", "down", "left", "right"] as const) {
  registerGlyph(`chevron-${direction}`, (g) => chevron(g, direction));
  registerGlyph(`triangle-${direction}`, (g) =>
    drawTriangle(g.rc, g.box, direction, g.ink, Math.max(3, Math.round(side(g.box) * 0.28))),
  );
}

registerGlyph("search", (g) => {
  const c = center(g.box);
  const r = side(g.box) * 0.24;
  // The lens is a rounded rect with its radius at half the side, which is a
  // circle, drawn as a ring by giving it a border and no fill.
  const cx = c.x - r * 0.25;
  const cy = c.y - r * 0.25;
  g.rc.drawRect(
    { x: cx - r, y: cy - r, width: r * 2, height: r * 2 },
    { borderWidth: g.weight, borderColor: g.ink, radius: r },
  );
  const from = r * 0.72;
  g.rc.drawLine(
    { x: cx + from, y: cy + from },
    { x: c.x + r * 1.1, y: c.y + r * 1.1 },
    g.weight,
    g.ink,
  );
});

// --- media transport -------------------------------------------------------
//
// A media control is built from ordinary buttons, so its faces come from here
// rather than from the control: the same names work on a toolbar, and a theme
// or an application can replace any of them without touching the player.

registerGlyph("play", (g) =>
  drawTriangle(g.rc, g.box, "right", g.ink, Math.max(4, Math.round(side(g.box) * 0.34))),
);

registerGlyph("pause", (g) => {
  const c = center(g.box);
  const h = Math.round(side(g.box) * 0.62);
  const w = Math.max(2, Math.round(side(g.box) * 0.16));
  const gap = Math.max(2, Math.round(side(g.box) * 0.16));
  for (const sign of [-1, 1]) {
    g.rc.fillRect(
      { x: Math.round(c.x + (sign * gap) / 2 - (sign < 0 ? w : 0)), y: Math.round(c.y - h / 2), width: w, height: h },
      g.ink,
    );
  }
});

registerGlyph("stop", (g) => {
  const c = center(g.box);
  const s = Math.round(side(g.box) * 0.52);
  g.rc.fillRect({ x: Math.round(c.x - s / 2), y: Math.round(c.y - s / 2), width: s, height: s }, g.ink, 1);
});

/**
 * The cone every speaker glyph shares, and the point its waves radiate from.
 *
 * The wedge is `left`-pointing: the apex is the narrow end against the driver
 * and the base is the mouth. Pointing it `right` — which is the direction the
 * sound goes, and the mistake that is easy to make — draws a funnel.
 */
function speaker(g: GlyphContext): { x: number; y: number; radius: number } {
  const c = center(g.box);
  const s = side(g.box);
  const mouth = c.x + s * 0.02;

  // The driver: a small slab behind the cone.
  g.rc.fillRect(
    {
      x: Math.round(c.x - s * 0.42),
      y: Math.round(c.y - s * 0.15),
      width: Math.round(s * 0.22),
      height: Math.round(s * 0.3),
    },
    g.ink,
  );
  g.rc.fillTriangle(
    {
      x: Math.round(c.x - s * 0.3),
      y: Math.round(c.y - s * 0.31),
      width: Math.round(s * 0.32),
      height: Math.round(s * 0.62),
    },
    "left",
    g.ink,
  );
  return { x: mouth, y: c.y, radius: s * 0.16 };
}

registerGlyph("volume", (g) => {
  const from = speaker(g);
  // Two arcs opening away from the mouth, drawn as thin annulus wedges.
  for (let i = 1; i <= 2; i++) {
    const r = from.radius * (0.9 + i * 0.75);
    const half = Math.max(0.6, g.weight / 2);
    g.rc.fillArc({ x: from.x, y: from.y }, r - half, r + half, -Math.PI / 5, Math.PI / 5, g.ink);
  }
});

registerGlyph("mute", (g) => {
  const from = speaker(g);
  // A cross where the waves would be, so the two read as a pair.
  const s = from.radius * 1.1;
  const x = from.x + s * 1.1;
  g.rc.drawLine({ x: x - s / 2, y: from.y - s / 2 }, { x: x + s / 2, y: from.y + s / 2 }, g.weight, g.ink);
  g.rc.drawLine({ x: x - s / 2, y: from.y + s / 2 }, { x: x + s / 2, y: from.y - s / 2 }, g.weight, g.ink);
});
